'use client'

import React, { useEffect, useRef, useState } from 'react'
import Link from 'next/link'

type V = { x: number; y: number }
type Pulse = {
  x: number
  y: number
  r: number
  prevR: number
  life: number
  speed: number
}
type Particle = { x: number; y: number; vx: number; vy: number; life: number }
type Star = { x: number; y: number; tw: number; p: number }

const W = 360
const H = 360

function clamp(n: number, a: number, b: number) {
  return Math.max(a, Math.min(b, n))
}

function rand(min: number, max: number) {
  return Math.random() * (max - min) + min
}

function dist(a: V, b: V) {
  const dx = a.x - b.x
  const dy = a.y - b.y
  return Math.hypot(dx, dy)
}

const NotFoundSonar: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  // game refs (mutable, no extra renders)
  const pulsesRef = useRef<Pulse[]>([])
  const particlesRef = useRef<Particle[]>([])
  const scannerRef = useRef<V>({ x: W / 2, y: H / 2 })
  const portalRef = useRef<V>({ x: rand(40, W - 40), y: rand(40, H - 40) })
  const portalFlashRef = useRef(0) // 0..1 flash intensity
  const lockRef = useRef(0) // 0..1 lock meter
  const starsRef = useRef<Star[]>(
    Array.from({ length: 42 }, () => ({
      x: rand(0, W),
      y: rand(0, H),
      tw: rand(0.6, 1.6), // twinkle speed
      p: rand(0, Math.PI * 2), // phase
    })),
  )
  const rafRef = useRef<number | null>(null)

  // state shown in UI
  const [lock, setLock] = useState(0) // 0..1
  const [hasWon, setHasWon] = useState(false)
  const [online, setOnline] = useState(true)
  const [usingKeys, setUsingKeys] = useState(false)

  // mirror usingKeys into a ref so the RAF effect doesn't depend on it
  const usingKeysRef = useRef(false)
  useEffect(() => {
    usingKeysRef.current = usingKeys
  }, [usingKeys])

  // reset for "play again"
  const reset = () => {
    pulsesRef.current = []
    particlesRef.current = []
    lockRef.current = 0
    setLock(0)
    portalRef.current = { x: rand(40, W - 40), y: rand(40, H - 40) }
    portalFlashRef.current = 0
    setHasWon(false)
  }

  // online/offline
  useEffect(() => {
    const update = () => setOnline(navigator.onLine)
    window.addEventListener('online', update)
    window.addEventListener('offline', update)
    update()
    return () => {
      window.removeEventListener('online', update)
      window.removeEventListener('offline', update)
    }
  }, [])

  // keyboard controls
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      usingKeysRef.current = true // immediate visual feedback
      setUsingKeys(true)

      const s = scannerRef.current
      const step = 12
      if (e.key === 'ArrowUp') s.y = clamp(s.y - step, 12, H - 12)
      if (e.key === 'ArrowDown') s.y = clamp(s.y + step, 12, H - 12)
      if (e.key === 'ArrowLeft') s.x = clamp(s.x - step, 12, W - 12)
      if (e.key === 'ArrowRight') s.x = clamp(s.x + step, 12, W - 12)
      if (e.key === ' ') {
        // ping from scanner
        pulsesRef.current.push({
          x: s.x,
          y: s.y,
          r: 0,
          prevR: 0,
          life: 1,
          speed: 2.6,
        })
        e.preventDefault()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // main loop
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const dpr = Math.max(1, window.devicePixelRatio || 1)
    canvas.width = W * dpr
    canvas.height = H * dpr
    canvas.style.width = `${W}px`
    canvas.style.height = `${H}px`

    let last = performance.now()

    const tick = (t: number) => {
      const dt = Math.min(33, t - last) // cap delta for stability
      last = t

      // reset transform each frame for crisp scaling
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

      // --- background ---
      const grad = ctx.createLinearGradient(0, 0, 0, H)
      grad.addColorStop(0, '#0b1024')
      grad.addColorStop(1, '#1b2a4a')
      ctx.fillStyle = grad
      ctx.fillRect(0, 0, W, H)

      // subtle vignette
      const v = ctx.createRadialGradient(
        W / 2,
        H / 2,
        40,
        W / 2,
        H / 2,
        Math.max(W, H),
      )
      v.addColorStop(0, 'rgba(0,0,0,0)')
      v.addColorStop(1, 'rgba(0,0,0,0.35)')
      ctx.fillStyle = v
      ctx.fillRect(0, 0, W, H)

      // stars twinkling
      ctx.save()
      for (const s of starsRef.current) {
        const a = 0.35 + 0.35 * Math.sin(t * 0.0015 * s.tw + s.p)
        ctx.globalAlpha = a
        ctx.fillStyle = '#a7c4ff'
        ctx.fillRect(s.x, s.y, 1, 1)
      }
      ctx.restore()

      // --- pulses ---
      const portal = portalRef.current
      const pulses = pulsesRef.current
      for (let i = pulses.length - 1; i >= 0; i--) {
        const p = pulses[i]
        p.prevR = p.r
        p.r += p.speed * (dt * 0.06) // speed is pixels per frame-ish
        p.life -= dt * 0.00065

        // ring style
        ctx.save()
        ctx.globalAlpha = Math.max(0, p.life)
        ctx.beginPath()
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2)
        ctx.strokeStyle = '#88ffd1'
        ctx.lineWidth = 2
        ctx.setLineDash([6, 6])
        ctx.stroke()
        ctx.restore()

        // echo trigger: ring crosses portal distance
        const d = dist({ x: p.x, y: p.y }, portal)
        if (!hasWon && p.prevR < d && p.r >= d) {
          // flash the portal and charge lock
          portalFlashRef.current = 1
          lockRef.current = Math.min(1, lockRef.current + 0.22)
          setLock(lockRef.current)

          // a few spark particles
          for (let k = 0; k < 14; k++) {
            particlesRef.current.push({
              x: portal.x,
              y: portal.y,
              vx: Math.cos((k / 14) * Math.PI * 2) * rand(0.6, 2.8),
              vy: Math.sin((k / 14) * Math.PI * 2) * rand(0.6, 2.8),
              life: 1,
            })
          }
        }

        // cleanup dead pulses
        if (p.life <= 0 || p.r > Math.hypot(W, H)) {
          pulses.splice(i, 1)
        }
      }

      // particles (sparkles on echo)
      const parts = particlesRef.current
      for (let i = parts.length - 1; i >= 0; i--) {
        const pt = parts[i]
        pt.x += pt.vx
        pt.y += pt.vy
        pt.vx *= 0.98
        pt.vy *= 0.98
        pt.life -= dt * 0.0019

        ctx.save()
        ctx.globalAlpha = Math.max(0, pt.life)
        ctx.fillStyle = '#c7fff1'
        ctx.fillRect(pt.x, pt.y, 2, 2)
        ctx.restore()

        if (pt.life <= 0) parts.splice(i, 1)
      }

      // portal hint shimmer (only subtle until unlocked)
      if (!hasWon) {
        const hintAlpha = 0.06 + 0.04 * Math.sin(t * 0.002)
        ctx.save()
        ctx.globalAlpha = hintAlpha
        ctx.beginPath()
        ctx.arc(portal.x, portal.y, 10, 0, Math.PI * 2)
        ctx.fillStyle = '#63ffd2'
        ctx.fill()
        ctx.restore()
      }

      // portal flash on echo & unlocked visual
      if (portalFlashRef.current > 0 || hasWon) {
        const pulse = hasWon ? 1 : portalFlashRef.current
        const glowR = hasWon ? 28 + 12 * Math.sin(t * 0.01) : 18
        ctx.save()
        ctx.globalAlpha = 0.3 + 0.4 * pulse
        ctx.beginPath()
        ctx.arc(portal.x, portal.y, glowR, 0, Math.PI * 2)
        ctx.fillStyle = hasWon ? '#7cffea' : '#88ffd1'
        ctx.fill()
        ctx.restore()

        portalFlashRef.current *= 0.92
      }

      // scanner reticle (for keyboard users)
      ctx.save()
      ctx.translate(scannerRef.current.x, scannerRef.current.y)
      ctx.globalAlpha = usingKeysRef.current ? 0.9 : 0.4 // <- read from ref
      ctx.strokeStyle = '#8ecaff'
      ctx.lineWidth = 1.5
      ctx.beginPath()
      ctx.arc(0, 0, 10, 0, Math.PI * 2)
      ctx.stroke()
      ctx.beginPath()
      ctx.moveTo(-12, 0)
      ctx.lineTo(12, 0)
      ctx.moveTo(0, -12)
      ctx.lineTo(0, 12)
      ctx.stroke()
      ctx.restore()

      // win condition
      if (!hasWon && lockRef.current >= 1) {
        setHasWon(true)
      }

      rafRef.current = requestAnimationFrame(tick)
    }

    rafRef.current = requestAnimationFrame(tick)
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [hasWon]) // stays stable; no usingKeys here

  // clicks/taps add pulses
  const handlePing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const x = clamp(e.clientX - rect.left, 0, W)
    const y = clamp(e.clientY - rect.top, 0, H)
    pulsesRef.current.push({
      x,
      y,
      r: 0,
      prevR: 0,
      life: 1,
      speed: 2 + Math.random() * 1.6,
    })
  }

  return (
    <div
      role="main"
      aria-label="404 sonar search mini-game"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background:
          'radial-gradient(1200px 600px at 20% -10%, #182a52 0%, #0f1933 40%, #0a1124 100%)',
        color: '#e8f0ff',
        padding: '24px',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <div
          style={{
            position: 'relative',
            borderRadius: 20,
            boxShadow:
              '0 8px 40px rgba(0,0,0,0.45), 0 0 0 1px rgba(120,150,255,0.15)',
            background:
              'linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))',
            padding: 12,
          }}
        >
          <canvas
            ref={canvasRef}
            width={W}
            height={H}
            onClick={handlePing}
            style={{
              display: 'block',
              width: W,
              height: H,
              borderRadius: 16,
              cursor: 'crosshair',
              background: '#0d1530',
            }}
            aria-label="Sonar canvas. Click to ping the void and locate the portal."
          />

          {/* Lock meter */}
          <div
            aria-label="Lock meter"
            style={{
              position: 'absolute',
              left: 16,
              right: 16,
              bottom: 16,
              height: 10,
              borderRadius: 8,
              background: 'rgba(255,255,255,0.08)',
              overflow: 'hidden',
              boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.06)',
            }}
          >
            <div
              style={{
                width: `${Math.round(lock * 100)}%`,
                height: '100%',
                background:
                  'linear-gradient(90deg, #3cf5bd 0%, #77ffe6 60%, #c8fff6 100%)',
                transition: 'width 160ms ease-out',
              }}
            />
          </div>
        </div>

        <h1
          style={{
            margin: '14px 0 4px',
            fontSize: '2.2rem',
            letterSpacing: 0.3,
          }}
        >
          {hasWon ? 'Portal Triangulated' : '404: Signal Lost'}
        </h1>

        <p style={{ margin: 0, opacity: 0.85 }}>
          {online
            ? hasWon
              ? 'Echoes converged. Corridor is open.'
              : 'Ping the void. Watch where the echoes appear. Triangulate.'
            : 'You’re offline. The void is quiet-try again once you’re reconnected.'}
        </p>

        {!hasWon && (
          <div
            style={{
              marginTop: 10,
              fontSize: 13,
              opacity: 0.7,
              textAlign: 'center',
              lineHeight: 1.4,
            }}
          >
            <div>🖱️ Click anywhere to send a sonar pulse.</div>
            <div>
              ⌨️ Arrow keys move the scanner • Space to ping from the scanner.
            </div>
          </div>
        )}

        {/* The way home is always available — the minigame is optional and
            must never gate navigation back to the site. */}
        <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
          <Link
            href="/"
            style={{
              padding: '10px 18px',
              borderRadius: 14,
              background: '#2bdeaa',
              color: '#001a12',
              fontWeight: 600,
              textDecoration: 'none',
              boxShadow: '0 6px 24px rgba(59, 255, 210, 0.25)',
            }}
          >
            {hasWon ? 'Return to Safety' : 'Return home'}
          </Link>
          {hasWon && (
            <button
              onClick={reset}
              style={{
                padding: '10px 16px',
                borderRadius: 14,
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.25)',
                color: '#e8f0ff',
                cursor: 'pointer',
              }}
            >
              Play Again
            </button>
          )}
        </div>

        {!online && (
          <div style={{ marginTop: 12, fontSize: 12, opacity: 0.7 }}>
            Tip: the lock meter won’t charge while offline.
          </div>
        )}
      </div>
    </div>
  )
}

export default NotFoundSonar
